// @ts-check

/**
 * R57c: the cart controller.
 *
 * Every cart mutation in this theme used to be hand-rolled by its initiator:
 * build the body, fetch, parse, morph, dispatch, hope. Seven initiators, seven
 * slightly different versions of the same twenty lines, and each one a place
 * for the pipeline to drift (R56d dispatched at the window, R57a addressed
 * lines by array position, R57b-4 found three PDP paths that morphed sections
 * but never told the badge). This module owns the whole round trip so there is
 * exactly one version of it. Initiators become thin callers: hand it a body,
 * get back a result.
 *
 * Deliberately a plain script rather than a module, like cart-sections.js: it
 * is consumed by Alpine components and inline section scripts that are not on
 * the importmap, so it has to be reachable as a global.
 *
 * Depends on window.cartSections for registry discovery and item counting, and
 * on window.fetchConfig (exposed by snippets/ctr-cart-drawer.liquid, present on
 * every page via the drawer section). Both are resolved at call time, never at
 * definition time, so load order between the two files does not matter.
 */
(function () {
  'use strict';

  /**
   * How long to wait before re-reading cart state after a request that failed
   * in a way that looks like rate limiting. Shopify answers repeated cart calls
   * with a 429 carrying an HTML body; reconciling immediately just earns
   * another one.
   */
  const RECONCILE_BACKOFF_MS = 1500;
  const DYNAMIC_BOX_WAIT_MS = 10000;
  const DYNAMIC_BOX_PLAN_IDS = new Set([2274525299, 2274558067, 2274590835, 2274623603]);

  /**
   * R91, Codex round 37 finding 6. HTTP codes whose failure is TRANSIENT, so the
   * caller must be free to try again. Everything here is classified as a transport
   * failure even when the body looks like a cart error, because a refusal is a
   * decision that will be repeated identically and these are not.
   *   408 request timeout, 409 cart concurrency conflict, 429 rate limited,
   *   5xx server-side.
   * @param {number} status
   */
  const RETRYABLE_STATUSES = {
    has: (status) => status === 408 || status === 409 || status === 429 || status >= 500,
  };

  /**
   * R91, Codex round 37 finding 5. Whether a parsed 2xx body is recognisably
   * something the cart endpoints return, as opposed to `{}` or a document Shopify
   * content-negotiated into the response.
   *
   * Deliberately PERMISSIVE. `/cart/change.js` answers with the whole cart
   * (`items`, `item_count`, `token`); `/cart/add.js` answers with the added LINE
   * (`key`, `id`, `variant_id`); either may carry `sections`. Being strict here
   * would fail real adds, which is far worse than the shape this catches, so the
   * test only has to be tight enough to reject a body carrying none of them.
   * @param {Record<string, any>} data
   * @returns {boolean}
   */
  function isCartDocument(data) {
    if (!data || typeof data !== 'object') return false;

    return (
      Array.isArray(data.items) ||
      typeof data.item_count === 'number' ||
      typeof data.token === 'string' ||
      data.key !== undefined ||
      data.id !== undefined ||
      data.variant_id !== undefined ||
      data.sections !== undefined
    );
  }

  /**
   * R89: the customer-facing failure surface.
   *
   * Measured on the dev theme 20 Aug: clicking Subscribe & Save on either buy
   * box produced NO /cart/add.js request, no cart change, and one console.error
   * - so to a customer the button was simply dead. Every failure path in this
   * file ended at console.error, and cart-drawer-alp.js:1047 had a Toastify
   * call with zero callers. Nothing in the theme could tell a customer that
   * anything had gone wrong.
   *
   * The surface lives here rather than in either buy box because add(), change()
   * and changeLines() are the choke point every initiator already goes through.
   * One implementation, and a new initiator gets it for free.
   *
   * **Do not route this through Toastify.** That was the first attempt, because
   * at the time window.Toastify and a toastify stylesheet were loaded on every
   * page. (They no longer are: R-TOAST, 6 Sep 2026, removed the library, its
   * inline CSS, the disabled toast feature and its settings from the theme.)
   * It did not work: an inline stylesheet in this theme carried
   * `.toastify-bottom { bottom: -150px }`, and the rendered toast measured a
   * stable `top: 934px` in an 830px viewport on three consecutive runs - fully
   * below the fold. Setting `bottom` inline with `!important` did not move it.
   * The orphan caller is the reason nobody ever noticed: the library, its CSS
   * and its one call site have all been dead at the same time. Shipping through
   * it would have replaced a silent failure with an invisible message, which is
   * the same bug wearing a hat. The banner below owns its own styles instead.
   *
   * Two channels:
   *   1. A visually-hidden aria-live region, created once at load. Assistive
   *      technology only announces changes to a region that was already in the
   *      DOM, so it cannot be created and filled in the same tick.
   *   2. A self-styled banner, aria-hidden so the region above is the only
   *      thing announcing - two channels reading the same string reads it twice.
   *
   * A `cart:error` CustomEvent is dispatched before either renders, cancelable:
   * calling preventDefault() on it suppresses the default rendering, so a page
   * that wants to show the failure inline can take ownership without this file
   * knowing about it.
   */
  const ERROR_EVENT = 'cart:error';
  const GENERIC_ERROR = 'Sorry, your basket could not be updated. Please try again.';
  // swapLines(): the swap failed AND putting the original lines back failed too.
  const LINES_NOT_RESTORED_ERROR =
    'Sorry, some items could not be put back in your basket. Please check it before you pay.';
  const SUBSCRIPTION_ERROR =
    'We could not set up that subscription. Please refresh the page and try again, or choose one-time purchase.';
  const BANNER_ID = 'cart-error-banner';
  const BANNER_STYLE_ID = 'cart-error-banner-styles';
  const BANNER_MS = 7000;

  /** @type {HTMLElement | null} */
  let liveRegion = null;
  /** @type {number | null} */
  let bannerTimer = null;

  /** @returns {HTMLElement | null} */
  function ensureLiveRegion() {
    if (liveRegion && liveRegion.isConnected) return liveRegion;
    if (!document.body) return null;

    liveRegion = /** @type {HTMLElement | null} */ (document.getElementById('cart-error-live'));

    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'cart-error-live';
      liveRegion.setAttribute('role', 'status');
      liveRegion.setAttribute('aria-live', 'assertive');
      liveRegion.setAttribute('aria-atomic', 'true');
      liveRegion.style.cssText =
        'position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;border:0;';
      document.body.appendChild(liveRegion);
    }

    return liveRegion;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureLiveRegion, { once: true });
  } else {
    ensureLiveRegion();
  }

  /**
   * Clearing first matters: setting the same string twice is not a change, and
   * a customer who clicks a broken button twice deserves to hear it twice.
   * @param {string} message
   */
  function announce(message) {
    const region = ensureLiveRegion();
    if (!region) return;

    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 60);
  }

  /**
   * The banner's styles live in one injected block rather than inline so the
   * mobile offset can be a media query: the sticky ATC bar owns the bottom of
   * a small viewport, and a banner underneath it is as good as no banner.
   */
  function ensureBannerStyles() {
    if (document.getElementById(BANNER_STYLE_ID)) return;
    if (!document.head) return;

    const style = document.createElement('style');
    style.id = BANNER_STYLE_ID;
    style.textContent = [
      '#' + BANNER_ID + '{position:fixed;left:50%;bottom:24px;transform:translateX(-50%);',
      'z-index:2147483647;display:flex;align-items:flex-start;gap:12px;',
      'max-width:min(92vw,460px);padding:12px 14px;background:#8a1c1c;color:#fff;',
      'font:500 14px/1.4 inherit;border-radius:4px;box-shadow:0 4px 16px rgba(0,0,0,.25);}',
      '#' + BANNER_ID + ' button{all:unset;cursor:pointer;flex:0 0 auto;padding:0 2px;',
      'font-size:16px;line-height:1.2;color:#fff;}',
      '@media (max-width:749px){#' + BANNER_ID + '{left:16px;right:16px;transform:none;',
      'max-width:none;bottom:calc(96px + env(safe-area-inset-bottom,0px));}}',
    ].join('');
    document.head.appendChild(style);
  }

  /** @param {string} message */
  function renderBanner(message) {
    if (!document.body) return;

    ensureBannerStyles();

    const existing = document.getElementById(BANNER_ID);
    if (existing) existing.remove();
    if (bannerTimer) window.clearTimeout(bannerTimer);

    const banner = document.createElement('div');
    banner.id = BANNER_ID;
    // The live region is the accessible channel. This is the visual one.
    banner.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.textContent = message;

    const close = document.createElement('button');
    close.type = 'button';
    close.textContent = '✕';
    close.setAttribute('aria-label', 'Dismiss');
    close.addEventListener('click', () => banner.remove());

    banner.appendChild(text);
    banner.appendChild(close);
    document.body.appendChild(banner);

    bannerTimer = window.setTimeout(() => banner.remove(), BANNER_MS);
  }

  /**
   * Tells the customer that a cart operation failed.
   * @param {string} [message] - customer-facing copy. Never pass raw error text.
   * @param {{source?: string, error?: unknown, data?: Record<string, any> | null}} [info]
   * @returns {string} the message that was surfaced
   */
  function surfaceError(message, info) {
    const text = (typeof message === 'string' && message.trim()) || GENERIC_ERROR;

    const event = new CustomEvent(ERROR_EVENT, {
      bubbles: true,
      cancelable: true,
      detail: {
        message: text,
        source: info?.source || 'cart-controller',
        error: info?.error ?? null,
        data: info?.data ?? null,
      },
    });

    // dispatchEvent returns false when a listener called preventDefault().
    if (!document.dispatchEvent(event)) return text;

    announce(text);
    renderBanner(text);

    return text;
  }

  /**
   * R-STALEKEY. Shopify refusals written for a DEVELOPER rather than a customer.
   * R91 finding 7 established that Shopify's cart wording is usually better than
   * ours ("You can only add 3 of this item") and should be shown as it arrives.
   * That is still right, but it is not true of ALL of them, and the exceptions
   * are the ones that name the API's own parameters.
   *
   * Measured on LIVE `147519504499`, 19 Sep 2026: a `+` tap on a cart row during
   * a subscription conversion sends a line key the conversion has already
   * consumed, Shopify answers `400 {"errors":"no valid id or line parameter"}`,
   * and that string was rendered verbatim into `#cart-error-banner` and read out
   * by `#cart-error-live`. The customer has no id and no line parameter; they
   * have a plus button that did nothing.
   *
   * Matched on a normalised PREFIX because Shopify punctuates these
   * inconsistently - `line parameter is invalid.` carries a full stop and
   * `no valid id or line parameter` does not.
   */
  const MACHINE_REFUSALS = [
    'no valid id or line parameter',
    'line parameter is invalid',
    'cannot find variant',
  ];

  /**
   * @param {unknown} text
   * @returns {boolean} whether this refusal is addressed to a developer.
   */
  function isMachineRefusal(text) {
    if (typeof text !== 'string') return false;
    const normalised = text.trim().toLowerCase().replace(/[.!]+$/, '');
    return MACHINE_REFUSALS.some((phrase) => normalised.startsWith(phrase));
  }

  /**
   * Shopify's own cart errors are written for customers, so they are shown as
   * they arrive. Anything else falls back to house copy.
   * @param {Record<string, any> | null} data
   * @returns {string}
   */
  function customerMessage(data) {
    const candidate = data?.description || data?.message;
    if (typeof candidate === 'string' && candidate.trim() && !isMachineRefusal(candidate)) {
      return candidate;
    }

    // R91, Codex round 37 finding 7. `errors` is accepted as proof that a response
    // IS a refusal - isCartError tests it, and the drawer's refusal branch tests it -
    // but nothing ever read its contents, so an errors-only refusal was answered with
    // house copy and Shopify's actual wording was discarded. It arrives as
    // `{"errors": {"quantity": ["You can only add 3..."]}}`, so flatten one level and
    // take the strings. Shape-checked rather than assumed, because `errors` is
    // sometimes an array and its values are not guaranteed to be strings.
    const rendered = renderCartErrors(data?.errors);
    return rendered || GENERIC_ERROR;
  }

  /**
   * @param {unknown} errors
   * @returns {string} the customer-facing sentences in `errors`, or '' if none.
   */
  function renderCartErrors(errors) {
    if (!errors) return '';

    const values = Array.isArray(errors)
      ? errors
      : typeof errors === 'object'
        ? Object.values(errors)
        : [errors];

    // R-STALEKEY: the dead-key refusal arrives as a BARE STRING under `errors`
    // (`{"errors":"no valid id or line parameter"}`), not as the documented
    // `{key: [sentence]}` shape, so it reaches the customer through here rather
    // than through `description`. Both doors need the same lock.
    const sentences = values
      .flat()
      .filter((value) => typeof value === 'string' && value.trim() && !isMachineRefusal(value))
      .map((value) => value.trim());

    if (sentences.length === 0) return '';

    // Deduplicated: Shopify repeats the same sentence across keys often enough that
    // showing it twice in one banner is a real outcome, not a hypothetical one.
    return [...new Set(sentences)].join(' ');
  }

  /**
   * Bound the subscription precondition even if the store's request was
   * created by an older or replacement cart-drawer implementation that does
   * not abort its own fetch. The caller turns this rejection into the one
   * subscription failure surface.
   * @param {Promise<unknown>} request
   * @returns {Promise<void>}
   */
  async function waitForDynamicBox(request) {
    /** @type {number | null} */
    let timeout = null;

    try {
      await Promise.race([
        request,
        new Promise((_, reject) => {
          timeout = window.setTimeout(
            () => reject(new Error('Dynamic Box request timed out')),
            DYNAMIC_BOX_WAIT_MS
          );
        }),
      ]);
    } finally {
      if (timeout !== null) window.clearTimeout(timeout);
    }
  }

  /**
   * Every subscribed cart add enters through add(). Keep the Skio contract at
   * that boundary so a PDP cannot create a boxless line by omitting hidden
   * inputs or by selecting a plan from the legacy single-products group.
   * One-time bodies are returned untouched.
   * @param {Record<string, any>} body
   * @returns {Promise<Record<string, any>>}
   */
  async function withDynamicBox(body) {
    if (Array.isArray(body?.items)) {
      return { ...body, items: await Promise.all(body.items.map((item) => withDynamicBox(item))) };
    }

    const planId = Number(body?.selling_plan);
    if (!planId) return body;

    if (!DYNAMIC_BOX_PLAN_IDS.has(planId)) {
      throw new Error(`selling plan ${planId} is not an approved Dynamic Box plan`);
    }

    const store = window.Alpine?.store?.('cartDrawerStore');
    if (store?.dynamicBoxRequest) {
      await waitForDynamicBox(store.dynamicBoxRequest);
    }

    if (!store?.dynamicBoxAddReady || !store.dynamicBoxId) {
      throw new Error('Dynamic Box data is unavailable; subscription add was not sent');
    }

    return {
      ...body,
      properties: {
        ...(body.properties || {}),
        _dynamicBoxId: store.dynamicBoxId,
        _dynamicBoxIndex: 0,
      },
    };
  }

  /** @param {number} ms */
  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * A cart request whose response could not be understood at all - a network
   * failure, or a body that is not JSON (the 429-with-HTML case, where
   * JSON.parse throws "Unexpected token '<'"). This is NOT the same as a cart
   * error: Shopify reports those as valid JSON with a status/errors field, and
   * callers handle them from the returned data.
   */
  class CartTransportError extends Error {
    /**
     * @param {string} message
     * @param {{status?: number, body?: string, cause?: unknown}} [info]
     */
    constructor(message, info) {
      super(message);
      this.name = 'CartTransportError';
      this.status = info?.status ?? 0;
      this.body = info?.body ?? '';
      this.cause = info?.cause;
      // A 429 or a 5xx is worth backing off for. A malformed 200 is not going
      // to fix itself, but reconciling once is still the right move.
      this.rateLimited = this.status === 429 || this.status >= 500;
    }
  }

  /**
   * R57c-3: the single owner of everything that acts on the GLOBAL Alpine cart
   * store.
   *
   * Two `cartDrawer()` roots exist on build-a-box - the lifted drawer section and
   * the persistent basket - and before R57b-5 both registered the global store
   * listeners, so every `cart-drawer-store:update` ran `updateProducts` and
   * `reconcileSubscriptionState` twice, and because `_reconciling` is
   * instance-local both could independently launch the clear+add rebuild. R57b-5
   * fixed that with a bare `window.__exaltCartStoreOwner` boolean, which had one
   * weakness: release was unconditional on the claimant, so an owner root being
   * torn down and rebuilt could hand ownership to nobody, or to two roots in the
   * gap. Holding the owning INSTANCE here means a claim can only succeed when the
   * seat is genuinely empty, and a release can only be performed by whoever is
   * sitting in it.
   * @type {object | null}
   */
  let storeOwner = null;

  /**
   * @param {object} candidate
   * @returns {boolean} whether the candidate holds ownership after this call
   */
  function claimStoreOwner(candidate) {
    if (!candidate) return false;
    if (storeOwner === candidate) return true;
    if (storeOwner) return false;

    storeOwner = candidate;
    return true;
  }

  /** @param {object} candidate */
  function releaseStoreOwner(candidate) {
    if (storeOwner === candidate) storeOwner = null;
  }

  // No isStoreOwner() accessor. Nothing consumes one - cart-drawer-alp caches the
  // result of its own claim - and R57b-6 already made the case for not shipping an
  // API with no caller. Add it when something needs it.

  /** @returns {string[]} the registry's section ids */
  function ids() {
    return window.cartSections ? window.cartSections.ids() : [];
  }

  /** @returns {string} the registry's section ids as a mutation-body parameter */
  function sectionsParam() {
    return ids().join(',');
  }

  /**
   * Adds the registry sections to a mutation body. Every mutation asks for the
   * same set, which is the whole point of R57b: named cart files only, never
   * the header and never the grid.
   * @param {Record<string, any>} body
   * @returns {Record<string, any>}
   */
  function withSections(body) {
    const sections = sectionsParam();
    if (!sections) return body;

    return { ...body, sections, sections_url: window.location.pathname };
  }

  /**
   * @param {string} routeKey
   * @param {string} fallback
   * @returns {string}
   */
  function route(routeKey, fallback) {
    // @ts-ignore - Theme is a global defined in snippets/scripts.liquid
    return (typeof Theme !== 'undefined' && Theme.routes?.[routeKey]) || fallback;
  }

  /**
   * @param {Record<string, any> | null} body
   * @returns {RequestInit}
   */
  function requestOptions(body) {
    const serialised = JSON.stringify(body ?? {});

    if (window.fetchConfig) return window.fetchConfig('json', { body: serialised });

    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: serialised,
    };
  }

  /**
   * POSTs a cart mutation and returns the parsed body.
   * @param {string} url
   * @param {Record<string, any> | null} body
   * @returns {Promise<Record<string, any>>}
   * @throws {CartTransportError} when the response is not JSON
   */
  async function post(url, body) {
    let response;

    try {
      response = await fetch(url, requestOptions(body));
    } catch (error) {
      throw new CartTransportError(`Cart request to ${url} did not complete`, { cause: error });
    }

    const text = await response.text();

    /** @type {Record<string, any>} */
    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      // This is the 429 path. Shopify hands back an HTML challenge or error
      // page and the JSON parse blows up; the caller must not treat the
      // optimistic DOM as truth from here.
      throw new CartTransportError(`Cart response from ${url} was not JSON`, {
        status: response.status,
        body: text.slice(0, 200),
        cause: error,
      });
    }

    // R90c, Codex round 36 finding 2. `response.ok` was read nowhere: the only
    // question asked was "did this parse as JSON". A cart REFUSAL is JSON with a
    // `status`/`errors` field and callers turn it into Shopify's own wording via
    // isCartError, so that path is deliberately left alone here. What escaped was
    // the third shape - an HTTP error whose JSON body carries NEITHER field. It
    // reached settle() and was reported ok:true, which is a mutation that did not
    // happen being announced as one that did.
    //
    // R91, Codex round 37 finding 6. That test was too generous in the other
    // direction: `isCartError` only asks whether a `status`/`errors` field is
    // present, so ANY non-2xx JSON carrying one was classified as a REFUSAL - a
    // decision, final, not retried, intent consumed. A 409 conflict and a 5xx are
    // both transient and both carry a status. Retryability is a property of the
    // HTTP code, not of the body, so the code decides first.
    if (!response.ok && (RETRYABLE_STATUSES.has(response.status) || !isCartError(data))) {
      throw new CartTransportError(`Cart request to ${url} failed with HTTP ${response.status}`, {
        status: response.status,
        body: text.slice(0, 200),
      });
    }

    // R91, Codex round 37 finding 5. The status test above says nothing about a 2xx.
    // A 200 carrying `{}` - or any non-cart document - passed straight through:
    // isCartError is false, sectionsUsable is false, and settle() returned ok:true
    // for a mutation that never happened. readCart() has asked "is this a cart"
    // since R90c and post() never did, which was an inconsistency rather than a
    // decision. reconcile() already documents Shopify answering 200 with the wrong
    // document, so this is not a hypothetical shape.
    if (!isCartError(data) && !isCartDocument(data)) {
      throw new CartTransportError(`Cart response from ${url} was not a cart`, {
        status: response.status,
        body: text.slice(0, 200),
      });
    }

    return data;
  }

  /**
   * R57b-4, moved here from cart-sections.js: the one post-mutation pipeline.
   * Dispatches CartUpdateEvent on document - each cart-items-component morphs
   * its own surface from detail.data.sections, and cart-icon updates the badge
   * and its accessible labels from itemCount - then exactly one
   * cart-drawer-store:update.
   * @param {Record<string, string>} sections
   * @param {string} source
   * @returns {number} the post-mutation item count
   */
  function applyMutation(sections, source) {
    const itemCount = window.cartSections ? window.cartSections.readItemCount(sections) : 0;

    const event =
      typeof window.CartUpdateEvent === 'function'
        ? new window.CartUpdateEvent({}, source, { itemCount, source, sections })
        : new CustomEvent('cart:update', {
            bubbles: true,
            detail: { sourceId: source, data: { itemCount, source, sections } },
          });

    document.dispatchEvent(event);
    window.dispatchEvent(new CustomEvent('cart-drawer-store:update'));

    return itemCount;
  }

  /**
   * Re-renders every registered cart surface from the server and runs the
   * result through the normal pipeline.
   *
   * This is the honesty valve. The drawer removes a line row optimistically the
   * moment the customer clicks remove, so a request that then fails leaves the
   * page showing a cart the server does not have. Rather than trusting that
   * DOM, we throw it away and ask for the truth.
   *
   * One GET, all registry sections, via the same Section Rendering API the
   * mutations use.
   * @param {string} source
   * @param {number} [delayMs]
   * @returns {Promise<number | null>} the reconciled item count, or null if the
   *   reconcile itself could not complete
   */
  async function reconcile(source, delayMs) {
    const sectionIds = ids();
    if (sectionIds.length === 0) return null;

    if (delayMs) await sleep(delayMs);

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));

      // NO Accept header. Measured on the dev theme 16 Aug: sending
      // `Accept: application/json` to a /collections/* or /cart URL makes
      // Shopify content-negotiate the RESOURCE json - the 215-byte
      // {"collection": …} blob, or the whole of cart.js - and that shadows the
      // Section Rendering API completely. The request looks fine, the response
      // parses fine, and it contains no cart, so readItemCount falls through to
      // zero and the badge blanks on a cart that is not empty. The reconcile
      // was making things worse than doing nothing. Same URL with no Accept
      // header returns the section map, 59,407 B.
      const response = await fetch(url.toString());
      const text = await response.text();
      const sections = JSON.parse(text);

      // Never publish a count from a document we did not verify is the document
      // we asked for. This is the general form of the bug above: any future
      // endpoint change that quietly hands back something else now says so
      // instead of zeroing the badge.
      const missing = sectionIds.filter((id) => !(id in sections));
      if (missing.length > 0 || !sectionsUsable(sections)) {
        console.error(
          `[cart-controller] reconcile after ${source} got the wrong document; cart surfaces left as they were`,
          { asked: sectionIds, got: Object.keys(sections), missing }
        );
        return null;
      }

      return applyMutation(sections, `${source}:reconcile`);
    } catch (error) {
      // Nothing left to try. Say so loudly rather than leaving a silent lie on
      // screen: at least the console names the surface that is now stale.
      console.error(`[cart-controller] reconcile after ${source} failed; cart surfaces may be stale`, error);
      return null;
    }
  }

  /**
   * @typedef {object} CartResult
   * @property {boolean} ok - true only when the cart actually changed
   * @property {Record<string, any> | null} data - the parsed response, if there was one
   * @property {number | null} itemCount - the post-mutation count, if it could be read
   * @property {Error} [error] - set when the request never produced a usable response
   */

  /**
   * @param {Record<string, any>} data
   * @returns {boolean}
   */
  function isCartError(data) {
    return Boolean(data && (data.status || data.errors));
  }

  /**
   * Whether a bundled-sections response can be trusted as a complete picture of
   * the cart.
   *
   * Codex P2, R57c-1 review: bundled section rendering returns `null` for any
   * section it failed to render, so `{"cart-drawer": null}` is a truthy object
   * that carries no cart at all. Trusting it publishes an item count of zero -
   * `readItemCount` skips non-strings and falls through to its `return 0` - and
   * the badge goes blank on a cart that is not empty. An all-or-nothing check
   * costs one pass over two values and sends the partial case to `reconcile`,
   * which is exactly where it belongs.
   * @param {Record<string, any> | undefined} sections
   * @returns {boolean}
   */
  function sectionsUsable(sections) {
    if (!sections || typeof sections !== 'object') return false;

    const values = Object.values(sections);
    if (values.length === 0) return false;

    return values.every((html) => typeof html === 'string' && html.length > 0);
  }

  /**
   * Shared tail for every mutation: apply the returned sections, or go and
   * fetch them if the response did not carry a usable set.
   * @param {Record<string, any>} data
   * @param {string} source
   * @returns {Promise<CartResult>}
   */
  async function settle(data, source) {
    if (sectionsUsable(data.sections)) {
      return { ok: true, data, itemCount: applyMutation(data.sections, source) };
    }

    if (data.sections) {
      console.warn(`[cart-controller] ${source} got an incomplete sections response; re-reading cart state`, Object.keys(data.sections));
    }

    const itemCount = await reconcile(source, 0);
    return { ok: true, data, itemCount };
  }

  /**
   * @param {unknown} error
   * @param {string} source
   * @param {{surface?: boolean}} [options] - R90b: a caller that owns a RETRY
   *   loop must be able to suppress the per-attempt message and say one thing at
   *   the end. Only changeLines() passes this through; add() and change() are
   *   single-shot and always surface.
   * @returns {Promise<CartResult>}
   */
  async function recover(error, source, options) {
    console.error(`[cart-controller] ${source} mutation failed`, error);
    if (options?.surface !== false) surfaceError(GENERIC_ERROR, { source, error });

    const rateLimited = error instanceof CartTransportError && error.rateLimited;
    // Codex nit, R57c-1 review: the reconcile knows the real count. Returning
    // null threw away a number we had just paid a round trip for.
    const itemCount = await reconcile(source, rateLimited ? RECONCILE_BACKOFF_MS : 0);

    return {
      ok: false,
      data: null,
      itemCount,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  /**
   * Adds to the cart. Body is whatever /cart/add.js takes - a single
   * id/quantity/selling_plan, or an items array - minus the sections, which
   * this adds.
   * @param {Record<string, any>} body
   * @param {{source?: string}} [options]
   * @returns {Promise<CartResult>}
   */
  async function add(body, options) {
    const source = options?.source || 'cart-controller';

    let data;
    try {
      const safeBody = await withDynamicBox(body);
      data = await post(route('cart_add_url', '/cart/add.js'), withSections(safeBody));
    } catch (error) {
      if (error instanceof Error && /Dynamic Box|selling plan/.test(error.message)) {
        console.error(`[cart-controller] ${source} subscription add blocked:`, error.message);
        // R89: the guard is right to refuse - a boxless subscription is worse
        // than no subscription - but refusing in silence is what made this look
        // like a dead button. The customer gets house copy; the console keeps
        // the engineering detail.
        const message = surfaceError(SUBSCRIPTION_ERROR, { source, error });
        return { ok: false, data: { status: 422, description: message }, itemCount: null, error };
      }
      return recover(error, source);
    }

    if (isCartError(data)) {
      console.error(`[cart-controller] ${source} add rejected by Shopify:`, data.description || data.message || data.errors);
      surfaceError(customerMessage(data), { source, data });
      await reconcile(source, 0);
      return { ok: false, data, itemCount: null };
    }

    return settle(data, source);
  }

  /**
   * Changes one cart line. Address the line by its Shopify key (`id`), never by
   * its position in the list - R57a: list positions shuffle every time another
   * row goes away.
   * @param {Record<string, any>} body
   * @param {{source?: string, knownQuantity?: number}} [options] - R-STALEKEY:
   *   `knownQuantity` is the quantity the caller's rendered row held before this
   *   change. It is the ONLY thing that lets a dead key be re-addressed safely,
   *   and without it a dead key is reported rather than recovered. See
   *   retryWithFreshLineKey().
   * @returns {Promise<CartResult>}
   */
  async function change(body, options) {
    const source = options?.source || 'cart-controller';

    let data;
    try {
      data = await post(route('cart_change_url', '/cart/change'), withSections(body));
    } catch (error) {
      return recover(error, source);
    }

    if (isCartError(data)) {
      // R-STALEKEY. A dead line key is not a refusal, it is a stale address, and
      // the customer's intent is still valid. Try to deliver it before giving up.
      const recovered = await retryWithFreshLineKey(body, data, source, options?.knownQuantity);
      if (recovered) return recovered;

      console.error(`[cart-controller] ${source} change rejected by Shopify:`, data.description || data.message || data.errors);
      surfaceError(customerMessage(data), { source, data });
      await reconcile(source, 0);
      return { ok: false, data, itemCount: null };
    }

    return settle(data, source);
  }

  /**
   * R-STALEKEY (19 Sep 2026). THE CART HANDS ITS OWN STEPPERS AN ADDRESS THAT IS
   * ABOUT TO DIE, AND THE TAP WAS BEING THROWN AWAY WITH IT.
   *
   * A line key is a hash over variant, properties and selling plan, so APPLYING
   * OR REMOVING A PLAN CHANGES IT. `changeLines()` converts a cart one line at a
   * time and asks for NO sections in between, deliberately, so that N changes
   * cost one render rather than N - which means that for the whole conversion
   * every rendered row's `data-cart-line-key` names a line that no longer exists.
   * `serialise()` then makes this worse in the one way that matters: it queues
   * the customer's tap behind the conversion so it cannot 409, but the request
   * BODY was built at tap time, so it runs afterwards carrying the dead key.
   *
   * Reproduced on LIVE `147519504499`, 19 Sep 2026, five-line cart: conversion
   * changes at t+0 through t+2.9s, `+` tapped at t+3.8s sending the key consumed
   * at t+0, `400 {"errors":"no valid id or line parameter"}`, `cart:error` in the
   * red banner, and the line still at quantity 1. The window is one request per
   * line, so it widens with the basket.
   *
   * The key CANNOT be re-derived from the old one - the hash covers the very
   * fields the conversion changed - so the variant is the only thing that
   * survives, and a key is `<variant_id>:<hash>`. R57a's ban on addressing a line
   * by its POSITION is untouched: position is never consulted here.
   *
   * **A SINGLE SURVIVING LINE FOR THAT VARIANT IS NOT PROOF IT IS THE CUSTOMER'S
   * ROW, AND THE FIRST VERSION OF THIS FUNCTION EMPTIED A CART BECAUSE OF IT.**
   * Codex round CART-BUNDLING-STALEKEY finding 2, confirmed by measurement on dev
   * `147519373427`: a cart holding a subscription line of 3 and a one-time line of
   * 1 converts, the two MERGE into one line of 4, and a stale Remove for the
   * absorbed row then finds exactly one candidate and sends `quantity: 0` against
   * it - deleting all four, and returning `ok: true`. The customer asked to drop
   * one item and lost the basket. That is strictly worse than the banner this
   * exists to remove, and merges are not an edge case here: making the two add
   * paths agree is precisely what makes them merge.
   *
   * So the retry is gated on `options.knownQuantity`, the quantity the CALLER's
   * row was rendered with (`defaultValue`, which the morph sets from the server).
   * A line that merely changed key still holds that quantity; a line that ABSORBED
   * another holds more. Anything else, including a caller that does not supply the
   * number at all, is refused and falls through to reconcile-and-house-copy. Fails
   * closed, deliberately: losing a tap is recoverable, losing a basket is not.
   *
   * Runs inside the caller's serialised slot, so the conversion that invalidated
   * the key has already finished and `readCart()` is the settled truth.
   *
   * @param {Record<string, any>} body - the change body that was refused.
   * @param {Record<string, any>} data - Shopify's refusal.
   * @param {string} source
   * @param {number | undefined} knownQuantity - the quantity the caller's row held
   *   before this change. Without it there is no retry.
   * @returns {Promise<CartResult | null>} a settled result, or null to let the
   *   caller handle the refusal as it always has.
   */
  async function retryWithFreshLineKey(body, data, source, knownQuantity) {
    if (typeof body?.id !== 'string' || !body.id.includes(':')) return null;
    if (!Number.isFinite(knownQuantity)) return null;

    const refusal = data?.description || data?.message || data?.errors;
    const deadKey = Array.isArray(refusal)
      ? refusal.some(isMachineRefusal)
      : typeof refusal === 'object' && refusal !== null
        ? Object.values(refusal).flat().some(isMachineRefusal)
        : isMachineRefusal(refusal);
    if (!deadKey) return null;

    /** @type {Record<string, any>} */
    let cart;
    try {
      cart = await readCart();
    } catch (error) {
      // The read is the thing that failed, not the key. Say nothing here and let
      // the caller's existing reconcile-and-surface path own it.
      return null;
    }

    const items = cart.items || [];

    // The key is live after all, so this refusal is about something else and
    // re-sending the identical body would only earn the same answer.
    if (items.some((item) => item.key === body.id)) return null;

    const variantId = body.id.slice(0, body.id.indexOf(':'));
    const candidates = items.filter((item) => String(item.variant_id) === variantId);

    // Several lines of one variant differ only by their properties, and picking
    // between them would change a line the customer never touched.
    if (candidates.length !== 1) return null;

    // The absorbed-line guard. See the note above: equal quantity means the line
    // was re-keyed, a larger one means it swallowed the customer's row and every
    // absolute quantity computed against the old view is now wrong.
    if (candidates[0].quantity !== knownQuantity) {
      console.warn(
        `[cart-controller] ${source} line key had moved, but the surviving line for variant ${variantId} holds ${candidates[0].quantity} where the row held ${knownQuantity}; it has absorbed another line, so the change was NOT re-addressed`
      );
      return null;
    }

    const retryBody = { ...body, id: candidates[0].key };

    let retryData;
    try {
      retryData = await post(route('cart_change_url', '/cart/change'), withSections(retryBody));
    } catch (error) {
      return recover(error, source);
    }

    // One attempt only. A second refusal is a decision, not a stale address.
    //
    // Codex round CART-BUNDLING-STALEKEY finding 3: it is answered HERE rather than
    // by returning null. The retry reached the right line, so its refusal is the
    // one about the customer's actual request ("You can only add 3 of this item"),
    // while the caller still holds the dead-key response, which customerMessage()
    // now correctly reduces to house copy. Falling through would have thrown away
    // Shopify's real wording - the exact behaviour R91 finding 7 exists to keep.
    if (isCartError(retryData)) {
      console.error(`[cart-controller] ${source} re-addressed change rejected by Shopify:`, retryData.description || retryData.message || retryData.errors);
      surfaceError(customerMessage(retryData), { source, data: retryData });
      await reconcile(source, 0);
      return { ok: false, data: retryData, itemCount: null };
    }

    console.warn(
      `[cart-controller] ${source} addressed a line key that had already moved; re-resolved ${body.id} -> ${retryBody.id} and applied the change`
    );

    return settle(retryData, source);
  }

  /**
   * Reads the cart from the server. The one authoritative source of line keys.
   * @returns {Promise<Record<string, any>>}
   * @throws {CartTransportError}
   */
  async function readCart() {
    let response;

    try {
      response = await fetch(route('cart_url', '/cart') + '.js');
    } catch (error) {
      throw new CartTransportError('Cart read did not complete', { cause: error });
    }

    const text = await response.text();

    /** @type {Record<string, any>} */
    let data;
    try {
      data = JSON.parse(text);
    } catch (error) {
      throw new CartTransportError('Cart read was not JSON', {
        status: response.status,
        body: text.slice(0, 200),
        cause: error,
      });
    }

    // R90c, Codex round 36 finding 3. The same missing `response.ok`, and here it
    // is worse than a false success on one mutation. An HTTP error body parses,
    // has no `items`, and changeLines() then plans ZERO work against it - which
    // its own completion check reads as "nothing outstanding", so the sequence
    // returns ok:true having changed nothing. The caller in cart-drawer-alp.js
    // spends the customer's conversion intent on that and says nothing.
    //
    // A cart is `items` or it is not a cart. Both tests, because a 200 carrying
    // the wrong document is exactly the shape reconcile() already defends against.
    if (!response.ok) {
      throw new CartTransportError(`Cart read failed with HTTP ${response.status}`, {
        status: response.status,
        body: text.slice(0, 200),
      });
    }

    if (!data || !Array.isArray(data.items)) {
      throw new CartTransportError('Cart read returned a document with no items array', {
        status: response.status,
        body: text.slice(0, 200),
      });
    }

    return data;
  }

  /**
   * A stable fingerprint of the cart's line state, used only to detect whether a
   * change actually moved anything.
   * @param {Record<string, any>} cart
   * @returns {string}
   */
  function cartSignature(cart) {
    return (cart?.items || [])
      .map((item) => `${item.key}@${item.quantity}#${item.selling_plan_allocation?.selling_plan?.id ?? ''}`)
      .join('|');
  }

  /**
   * Applies a sequence of single-line changes, re-planning against the server
   * cart after every one.
   *
   * R57c-5, replacing `clearThenAdd`. That function emptied the cart and re-added
   * it wholesale because a comment in this file asserted that `/cart/change.js`
   * could not express a selling-plan switch. **That assertion was false** -
   * `selling_plan` has been a valid change parameter since February 2022, and it
   * was verified live on the dev theme: one POST with `{id: <key>, quantity,
   * selling_plan}` returned 200 with the plan applied and the price down by the
   * subscription discount. The old design's failure mode was fatal: a 200 on the
   * clear followed by a 429 on the add left a real customer with an empty server
   * cart and a page still drawing it as full, and nothing downstream could repair
   * it. Observed in the wild on 17 Aug. A sequence of line changes cannot do
   * that: every failure leaves a cart that is merely partially converted, and the
   * next attempt finishes the job.
   *
   * Three things make the loop safe:
   *
   * 1. **Intermediate changes ask for NO sections.** Only the final reconcile
   *    renders them, once, so N changes cost one drawer render instead of N.
   * 2. **Each response is the next source of truth.** A line's key is a hash over
   *    its variant, properties and selling plan, so applying a plan CHANGES THE
   *    KEY - measured, `…8f713e37…` became `…41c57…` - and two lines converging
   *    on the same variant/plan/properties merge into one. A work list computed
   *    once would be addressing lines that no longer exist by the second pass.
   * 3. **It gives up rather than spins.** The planner is re-run against the fresh
   *    cart every pass; if a pass changes nothing the loop stops, and an attempt
   *    cap derived from the starting line count bounds it regardless.
   *
   * @param {(cart: Record<string, any>) => Record<string, any>[]} planner - given
   *   the current server cart, return the change bodies still outstanding. Only
   *   the first is used per pass; the rest inform nothing and may be discarded.
   * @param {{source?: string, maxAttempts?: number, surface?: boolean}} [options]
   *   `surface: false` suppresses the customer-facing message for THIS call and
   *   nothing else - the console logging and the reconcile are unchanged, and
   *   the result still reports the failure. R90b, Codex round 35 finding 2: the
   *   cart-drawer conversion worker calls this up to six times for one customer
   *   intent, and surfacing per pass showed the same failure six times. A caller
   *   that suppresses owns the obligation to say something once itself.
   * @returns {Promise<CartResult & {applied: number}>}
   */
  async function changeLines(planner, options) {
    const source = options?.source || 'cart-controller';
    const surface = options?.surface !== false;
    const changeUrl = route('cart_change_url', '/cart/change');

    let cart;
    try {
      cart = await readCart();
    } catch (error) {
      const result = await recover(error, source, { surface });
      return { ...result, applied: 0 };
    }

    const cap = options?.maxAttempts ?? (cart.items || []).length * 2 + 2;
    let applied = 0;
    let attempts = 0;
    /** @type {Error | null} */
    let failure = null;
    /** @type {Record<string, any> | null} */
    let rejection = null;

    while (attempts < cap) {
      /** @type {Record<string, any>[]} */
      let work;
      try {
        work = planner(cart) || [];
      } catch (error) {
        // The planner reads and reshapes DOM state. A throw here used to be able
        // to strand an emptied cart; now it strands nothing, because nothing has
        // been cleared. Still reconcile so the surfaces match the server.
        failure = error instanceof Error ? error : new Error(String(error));
        break;
      }

      if (work.length === 0) break;

      attempts++;

      let data;
      try {
        // No sections. The drawer is rendered once, at the end.
        data = await post(changeUrl, work[0]);
      } catch (error) {
        failure = error instanceof Error ? error : new Error(String(error));
        break;
      }

      if (isCartError(data)) {
        console.error(
          `[cart-controller] ${source} line change rejected by Shopify:`,
          data.description || data.message || data.errors
        );
        rejection = data;
        break;
      }

      if (cartSignature(data) === cartSignature(cart)) {
        // The request succeeded and moved nothing. Re-planning would hand back
        // the same instruction for ever.
        console.error(
          `[cart-controller] ${source} line change made no progress; stopping with work outstanding`,
          work[0]
        );
        cart = data;
        break;
      }

      cart = data;
      applied++;
    }

    // Codex round 4, P2 + nit, and they are the same bug from two directions.
    // The old classification read how the LOOP EXITED rather than whether the
    // cart is actually converted:
    //   - the no-progress break set neither `failure` nor `rejection`, so a
    //     permanently stuck conversion returned ok:true, the caller cleared
    //     conversionFailed, and R57c-5d's intent guard silently disarmed itself
    //     on the one path that most needed it;
    //   - `attempts < cap` called a cap-sized-but-successful run a failure.
    // Asking the planner one more time answers both honestly: work left means
    // not ok, no work left means ok, however we got here.
    /** @type {number | null} */
    let outstanding = null;
    try {
      outstanding = (planner(cart) || []).length;
    } catch (error) {
      // A planner that throws on the final pass tells us nothing about the cart,
      // so fall back to pessimism rather than reporting a clean conversion.
      console.error(`[cart-controller] ${source} could not re-plan to verify completion`, error);
      outstanding = null;
    }

    if (outstanding !== 0) {
      console.error(
        `[cart-controller] ${source} finished with ${
          outstanding === null ? 'an unknown number of' : outstanding
        } line change(s) outstanding`
      );
    }

    if (failure) {
      console.error(`[cart-controller] ${source} line change sequence failed after ${applied} change(s)`, failure);
    }

    // R89: this loop does not go through recover(), so nothing has surfaced yet.
    // Surface the two ways a sequence can end badly, once each, and never twice
    // for the same failure.
    if (!surface) {
      // Nothing is said here. The caller asked to own the message because it is
      // going to try again, and it must surface once when it stops trying.
    } else if (rejection) {
      surfaceError(customerMessage(rejection), { source, data: rejection });
    } else if (failure || outstanding !== 0) {
      // R90: `outstanding` is null when the final verify re-plan THREW, and the
      // old `|| outstanding` treated that null as "nothing outstanding". So a
      // planner that blew up on the last pass returned ok:false (because
      // `outstanding === 0` is false) with nothing said to the customer - the
      // exact silent-failure class R89 exists to remove, surviving inside R89's
      // own surface. Testing against 0 makes "unknown" fail loud, like every
      // other unknown in this file.
      surfaceError(GENERIC_ERROR, { source, error: failure });
    }

    // One reconcile, whatever happened. On the happy path it publishes the final
    // cart to every surface; on a partial failure it publishes the partial truth,
    // which is a cart the customer still has.
    const rateLimited = failure instanceof CartTransportError && failure.rateLimited;
    const itemCount = await reconcile(source, rateLimited ? RECONCILE_BACKOFF_MS : 0);

    return {
      ok: !failure && !rejection && outstanding === 0,
      data: rejection || cart,
      itemCount,
      applied,
      outstanding,
      ...(failure ? { error: failure } : {}),
    };
  }

  /**
   * The body that puts one cart line back into the cart through /cart/add.js.
   * @param {Record<string, any>} line - a line as /cart.js reports it
   * @param {number | null} plan - the selling plan to add it on, null for one-time
   * @param {Record<string, any> | undefined} properties - replaces the line's own
   *   properties when non-empty; empty or absent keeps them, which is exactly what
   *   /cart/change.js does with an empty object, so the one-time direction ends in
   *   the same state it always has
   * @returns {Record<string, any>}
   */
  function lineAddBody(line, plan, properties) {
    const keep = !properties || Object.keys(properties).length === 0;
    return {
      id: line.variant_id,
      quantity: line.quantity,
      properties: keep ? line.properties || {} : properties,
      ...(plan ? { selling_plan: plan } : {}),
    };
  }

  /**
   * Switches every planned line in TWO requests, whatever the size of the basket:
   * remove them with /cart/update.js, then add them back on the new plan with one
   * /cart/add.js carrying all of them.
   *
   * new-cart-drawer, 21 Sep 2026, at Charlie's instruction: the old cart converted
   * in one go and changeLines(), one /cart/change.js per line, is what made the
   * switch slow. Measured on live, one 4-line basket: 1,498 ms this way against
   * 2,828 ms line by line, identical total and discount.
   *
   * THE REASON THE OLD ONE-GO VERSION WAS REMOVED IS THE THING THIS HAS TO SURVIVE.
   * `clearThenAdd` (replaced 17 Aug 2026, `baba1be`) emptied the cart, the re-add
   * was answered 429, and a real customer was left with an empty basket. Three
   * differences:
   *   - only the lines being switched are removed; merch, free lines and anything
   *     that cannot take the plan never leave the cart;
   *   - a failed add is followed by putting the originals back. Measured on live:
   *     a 422 on /cart/add.js adds NOTHING, even when only one item of several was
   *     refused, and re-adding the originals restores the basket exactly;
   *   - the restore is decided from a fresh read of the cart, not from how the
   *     request failed, so an ambiguous transport failure (the server may or may
   *     not have applied it) can neither lose a line nor double one: a planned line
   *     is only put back when neither its original nor its converted form is there.
   * Losing a line now needs the add AND the restore to fail.
   *
   * Removing first, not adding first, is deliberate: /cart/add.js counts what is
   * already in the cart against stock, so an add-first swap fails on any line at
   * its stock ceiling.
   *
   * Lines are added in reverse of the cart's order because the cart lists the
   * newest first; that keeps the switched lines in the order the customer saw them.
   * They do move above lines that were not switched.
   *
   * Same contract as changeLines(), so the conversion worker can call either.
   * @param {(cart: Record<string, any>) => Record<string, any>[]} planner
   * @param {{source?: string, surface?: boolean}} [options]
   * @returns {Promise<CartResult & {applied: number}>}
   */
  async function swapLines(planner, options) {
    const source = options?.source || 'cart-controller';
    const surface = options?.surface !== false;
    const updateUrl = route('cart_update_url', '/cart/update');
    const addUrl = route('cart_add_url', '/cart/add.js');

    let cart;
    try {
      cart = await readCart();
    } catch (error) {
      const result = await recover(error, source, { surface });
      return { ...result, applied: 0 };
    }

    let applied = 0;
    /** @type {Error | null} */
    let failure = null;
    /** @type {Record<string, any> | null} */
    let rejection = null;

    /** @type {Record<string, any>[]} */
    let work = [];
    try {
      work = planner(cart) || [];
    } catch (error) {
      failure = error instanceof Error ? error : new Error(String(error));
    }

    /** @type {Map<string, Record<string, any>>} */
    const byKey = new Map((cart.items || []).map((item) => [item.key, item]));
    const planned = work
      .map((change) => ({ change, line: byKey.get(change.id) }))
      .filter((entry) => entry.line);

    if (!failure && planned.length !== work.length) {
      failure = new Error('the planner named a line that is not in the cart');
    }

    if (!failure && planned.length > 0) {
      const ordered = [...planned].reverse();
      let attempted = false;

      try {
        const removed = await post(updateUrl, {
          updates: Object.fromEntries(planned.map((entry) => [entry.line.key, 0])),
        });
        attempted = true;
        if (isCartError(removed)) {
          // Refused outright: nothing was removed, so there is nothing to put back.
          rejection = removed;
          attempted = false;
        } else {
          const added = await post(
            addUrl,
            {
              items: ordered.map((entry) =>
                lineAddBody(entry.line, entry.change.selling_plan ?? null, entry.change.properties)
              ),
            }
          );
          if (isCartError(added)) {
            rejection = added;
          } else {
            applied = planned.length;
          }
        }
      } catch (error) {
        attempted = true;
        failure = error instanceof Error ? error : new Error(String(error));
      }

      let notRestored = false;
      if (attempted && applied === 0) {
        console.error(`[cart-controller] ${source} swap did not complete; putting the original lines back`, rejection || failure);
        try {
          const now = await readCart();
          // Codex round SWAP finding 1. "Is the converted form there?" has to mean
          // "did the swap ADD it", not "is there a line of that variant on that plan":
          // a basket can already hold the same drink subscribed and one-time, and
          // mistaking the existing line for the added one skipped the put-back and
          // lost the customer's quantity. So identify a line the way Shopify's key
          // does - variant, plan and properties - and require the quantity to have
          // GROWN by what was planned, measured against the cart before the swap.
          const planOf = (/** @type {Record<string, any>} */ item) =>
            Number(item.selling_plan_allocation?.selling_plan?.id) || null;
          const identity = (
            /** @type {number} */ variant,
            /** @type {number | null} */ plan,
            /** @type {Record<string, any>} */ properties
          ) =>
            `${variant}|${plan || ''}|${JSON.stringify(
              Object.keys(properties || {})
                .sort()
                .map((k) => [k, String(properties[k])])
            )}`;
          const quantityOf = (/** @type {Record<string, any>[]} */ items, /** @type {string} */ id) =>
            items
              .filter((item) => identity(item.variant_id, planOf(item), item.properties) === id)
              .reduce((sum, item) => sum + item.quantity, 0);

          const targets = ordered.map(({ change, line }) => {
            const body = lineAddBody(line, Number(change.selling_plan) || null, change.properties);
            return identity(line.variant_id, body.selling_plan || null, body.properties);
          });
          /** @type {Map<string, number>} */
          const plannedByTarget = new Map();
          ordered.forEach(({ line }, index) => {
            plannedByTarget.set(targets[index], (plannedByTarget.get(targets[index]) || 0) + line.quantity);
          });

          const missing = ordered.filter(({ line }, index) => {
            if ((now.items || []).some((item) => item.key === line.key)) return false;
            const id = targets[index];
            const added = quantityOf(now.items || [], id) - quantityOf(cart.items || [], id);
            return added < (plannedByTarget.get(id) || 0);
          });
          if (missing.length > 0) {
            const restored = await post(addUrl, {
              items: missing.map(({ line }) =>
                lineAddBody(line, Number(line.selling_plan_allocation?.selling_plan?.id) || null, undefined)
              ),
            });
            if (isCartError(restored)) {
              console.error(`[cart-controller] ${source} could not put ${missing.length} line(s) back`, restored);
              notRestored = true;
            }
          }
        } catch (error) {
          // Includes failing to READ the cart, when nobody knows what is in it.
          console.error(`[cart-controller] ${source} could not put the original lines back`, error);
          notRestored = true;
        }

        // Codex round SWAP-2 finding 1 (P1). Logging this was not enough: the worker
        // retried, found an emptied basket with nothing left to plan, and reported
        // the conversion a success. Returned as a REFUSAL so the worker stops and
        // tells the customer, whatever the original failure was.
        if (notRestored) {
          rejection = {
            status: 'lines-not-restored',
            message: 'Cart Error',
            description: LINES_NOT_RESTORED_ERROR,
          };
        }
      }
    }

    // Verify against the server, not against how the requests ended.
    /** @type {number | null} */
    let outstanding = null;
    try {
      cart = await readCart();
      outstanding = (planner(cart) || []).length;
    } catch (error) {
      console.error(`[cart-controller] ${source} could not re-plan to verify completion`, error);
      outstanding = null;
    }

    if (outstanding !== 0) {
      console.error(
        `[cart-controller] ${source} finished with ${
          outstanding === null ? 'an unknown number of' : outstanding
        } line change(s) outstanding`
      );
    }

    if (rejection) {
      console.error(
        `[cart-controller] ${source} swap rejected by Shopify:`,
        rejection.description || rejection.message || rejection.errors
      );
    }

    if (!surface) {
      // The caller owns the message, as with changeLines().
    } else if (rejection) {
      surfaceError(customerMessage(rejection), { source, data: rejection });
    } else if (failure || outstanding !== 0) {
      surfaceError(GENERIC_ERROR, { source, error: failure });
    }

    const rateLimited = failure instanceof CartTransportError && failure.rateLimited;
    const itemCount = await reconcile(source, rateLimited ? RECONCILE_BACKOFF_MS : 0);

    return {
      ok: !failure && !rejection && outstanding === 0,
      data: rejection || cart,
      itemCount,
      applied,
      outstanding,
      ...(failure ? { error: failure } : {}),
    };
  }

  /**
   * R-TAP, Codex round TAP-4 finding 1. ONE CART MUTATION AT A TIME, ACROSS EVERY
   * INITIATOR.
   *
   * The cart is a single server-side resource and Shopify answers concurrent
   * writes to it with 409, which post() classifies as a transport failure - so
   * one of the two taps becomes a banner instead of a change. Every initiator
   * already guarded its OWN overlaps (product-card-alp's pendingUpdate since
   * R57a, cart-items-component's per-line queue since R-TAP) and none of them
   * could see anyone else's, because this file was the only thing they shared
   * and it did not serialise.
   *
   * R-TAP made that reachable rather than theoretical. The drawer now opens in
   * the same task as the tap, so its steppers are on screen and live WHILE the
   * add that opened it is still in flight: tap Add on a PDP with an existing
   * basket, then tap `+` on a line in the drawer that has just slid open, and
   * that is an add and a change on one cart at once. It also closes the older
   * case two rapid taps on different product cards always had.
   *
   * Deliberately at the EXPORT boundary rather than inside each function: no
   * mutation in this file calls another (changeLines goes to readCart, recover
   * and post directly), and no caller in the theme awaits one mutation from
   * inside another - both checked, not assumed - so there is nothing that can
   * wait on itself. reconcile() and readCart() stay unserialised: they are GETs,
   * they are called from inside the mutations, and queueing them behind the
   * thing that triggered them WOULD deadlock.
   *
   * @type {Promise<unknown>}
   */
  let mutationQueue = Promise.resolve();

  /**
   * Runs a mutation once every mutation queued before it has settled.
   * @template T
   * @param {() => Promise<T>} run
   * @returns {Promise<T>}
   */
  function serialise(run) {
    // `then(run, run)` rather than `then(run)`: a rejected predecessor must not
    // strand every later mutation, and the caller still sees its own result.
    const started = mutationQueue.then(run, run);

    // The chain itself never carries a rejection forward.
    mutationQueue = started.then(
      () => undefined,
      () => undefined
    );

    return started;
  }

  window.cartController = {
    ids,
    sectionsParam,
    withSections,
    applyMutation,
    reconcile,
    /** @type {typeof add} */
    add: (body, options) => serialise(() => add(body, options)),
    /** @type {typeof change} */
    change: (body, options) => serialise(() => change(body, options)),
    readCart,
    /** @type {typeof changeLines} */
    changeLines: (planner, options) => serialise(() => changeLines(planner, options)),
    /** @type {typeof swapLines} */
    swapLines: (planner, options) => serialise(() => swapLines(planner, options)),
    isCartError,
    // R91, Codex round 37 finding 7: exported so a caller that owns its own message
    // - the cart-drawer conversion worker is the only one - renders a refusal the
    // same way this file does, including the `errors`-only shape. It was picking
    // description/message by hand and silently losing anything else.
    customerMessage,
    // Exported because §4 tells every new consumer to gate on this rather than
    // on a truthy `sections`, and it could not do that while it was private.
    sectionsUsable,
    claimStoreOwner,
    releaseStoreOwner,
    CartTransportError,
    // R89: exported so an initiator can surface its OWN failures - the ones that
    // never reach this file, like a click with no variant selected - through the
    // same channel, instead of inventing a second one.
    surfaceError,
    ERROR_EVENT,
    GENERIC_ERROR,
    SUBSCRIPTION_ERROR,
  };
})();
